<template>
  <div class="studio-panel">
    <h2>Pro Drop</h2>
    <p class="studio-panel-subtitle">Your rich syndicated drop - choose the reading, shape its focus with Aida, and set how it's labelled. Aida handles the rest.</p>

    <div v-if="loading" class="studio-loading">
      <span class="studio-loading-dot"></span><span class="studio-loading-text">Loading…</span>
    </div>
    <div v-if="loadError" class="studio-error">{{ loadError }}</div>

    <template v-if="!loading && setting">
      <!-- Drop-type chooser -->
      <div class="pro-types">
        <button v-for="t in TYPES" :key="t.key" type="button" class="pro-type"
          :class="{ on: setting.drop_type === t.key }" :disabled="switching" @click="chooseType(t.key)">
          <span class="pro-type-name">{{ t.label }}</span>
          <span class="pro-type-desc">{{ t.desc }}</span>
        </button>
      </div>

      <div v-if="setting.drop_type === 'natal' && !hasBirth" class="pro-warn">
        ⚠ Natal readings need your birth date, time &amp; place - add them in your profile to personalise to your chart.
      </div>

      <label v-if="setting.drop_type !== 'community'" class="pro-toggle">
        <input type="checkbox" v-model="setting.generate_tarot_drop" @change="saveSetting({ generate_tarot_drop: setting.generate_tarot_drop }, 'tarot')" />
        <span>Blend a tarot card (adds a card + illustration to the drop)</span>
      </label>

      <!-- Reading Focus & Tone — ONE plain-language field, assistant-edited. -->
      <div class="beat-field">
        <div class="beat-card" :class="{ 'beat-card-active': focusActive, 'beat-card-expanded': true }">
          <div class="beat-card-header">
            <span class="beat-head-left">
              <span class="beat-label-text">Reading Focus &amp; Tone</span>
              <span class="beat-label-help" @click.stop>
                <FieldHelpIcon field="pro:focus_intent" label="Reading Focus & Tone" panel="syndicate" :value="setting.focus_intent || ''" />
              </span>
            </span>
          </div>
          <div class="beat-card-body">
            <p class="beat-help">In your own words - what this reading emphasises and how it should feel. Aida writes the drop from this each day.</p>
            <textarea
              class="beat-textarea"
              :value="setting.focus_intent || ''"
              placeholder="e.g. Bold, founder-focused guidance - decisive moves, cutting through noise."
              rows="3"
              @input="setting.focus_intent = $event.target.value"
              @blur="saveSetting({ focus_intent: setting.focus_intent }, 'focus')"
            ></textarea>
            <div class="beat-card-actions">
              <button type="button" class="beat-btn beat-btn-refine" @click="refineFocus">
                ✨ {{ setting.focus_intent ? 'Refine with Aida' : 'Write with Aida' }}
              </button>
              <button type="button" class="beat-btn beat-btn-sim" @click="simulate" :disabled="simulating">
                {{ simulating ? '… Conjuring' : '▶ Simulate' }}
              </button>
              <span v-if="savedKey === 'focus'" class="beat-card-saved">Saved</span>
              <span v-if="saveError" class="studio-error">{{ saveError }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Illustration — art direction for the drop's image, seeded from the
           reader's illustrative prompt, overridable here. -->
      <div class="beat-field">
        <div class="beat-card" :class="{ 'beat-card-active': illoActive, 'beat-card-expanded': true }">
          <div class="beat-card-header">
            <span class="beat-head-left">
              <span class="beat-label-text">Illustration</span>
              <span class="beat-label-help" @click.stop>
                <FieldHelpIcon field="pro:illustration" label="Illustration" panel="syndicate" :value="setting.illustration_instructions || ''" />
              </span>
            </span>
          </div>
          <div class="beat-card-body">
            <p class="beat-help">The art direction for this drop's image. Seeded from your reader's illustrative style - change it up here anytime.</p>
            <textarea
              class="beat-textarea"
              :value="setting.illustration_instructions || ''"
              placeholder="e.g. Dreamy watercolor, moonlit, deep indigo and gold - no text in the image."
              rows="4"
              @input="setting.illustration_instructions = $event.target.value"
              @blur="saveSetting({ illustration_instructions: setting.illustration_instructions }, 'illustration')"
            ></textarea>
            <div class="beat-card-actions">
              <button type="button" class="beat-btn beat-btn-refine" @click="refineIllo">
                ✨ {{ setting.illustration_instructions ? 'Refine with Aida' : 'Write with Aida' }}
              </button>
              <span v-if="savedKey === 'illustration'" class="beat-card-saved">Saved</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Drop Labels — the seeker-facing names shown on the drop page. -->
      <section class="pro-labels">
        <h3 class="pro-labels-head">Drop Labels</h3>
        <p class="beat-help">What seekers see on your public drop - none of this is a prompt.</p>
        <div class="pro-label-grid">
          <label class="pro-label"><span>Drop name</span>
            <input v-model="group.drop_horoscope_alias" class="studio-input" placeholder="e.g. Daily Horoscope" @blur="saveLabels" /></label>
          <label class="pro-label"><span>Share button</span>
            <input v-model="group.share_label_alias" class="studio-input" placeholder="e.g. Read Today's Forecast" @blur="saveLabels" /></label>
          <label class="pro-label"><span>Card label</span>
            <input v-model="group.drop_title_type" class="studio-input" placeholder="e.g. Card" @blur="saveLabels" /></label>
          <label class="pro-label"><span>Daily label</span>
            <input v-model="group.horoscope_day_label" class="studio-input" placeholder="Today" @blur="saveLabels" /></label>
          <label class="pro-label"><span>Weekly label</span>
            <input v-model="group.horoscope_week_label" class="studio-input" placeholder="This Week" @blur="saveLabels" /></label>
          <label class="pro-label"><span>Monthly label</span>
            <input v-model="group.horoscope_month_label" class="studio-input" placeholder="This Month" @blur="saveLabels" /></label>
        </div>
        <span v-if="savedKey === 'labels'" class="beat-card-saved">Labels saved</span>
      </section>
    </template>

    <div v-if="!loading && !loadError && !setting" class="studio-stub"><p>No Pro drop found.</p></div>

    <!-- Simulate preview -->
    <div v-if="preview" class="pro-sim" @click.self="preview = null">
      <div class="pro-sim-card">
        <button class="pro-sim-close" @click="preview = null">×</button>
        <div class="pro-sim-label">Preview · {{ TYPES.find(t => t.key === setting.drop_type)?.label }}</div>
        <h3 class="pro-sim-title">{{ preview.title }}</h3>
        <p class="pro-sim-teaser">{{ preview.teaser }}</p>
        <p class="pro-sim-reading">{{ preview.reading_text }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import axios from 'axios'
import { store, static_store } from '@/store'
import { parseAxiosError } from '@/utils/general-utils'
import FieldHelpIcon from '../FieldHelpIcon.vue'
import { dispatchFieldContext, studioAssistantState } from '@/utils/studio-assistant'

const TYPES = [
  { key: 'zodiac', label: 'Zodiac', desc: 'A daily reading for all 12 signs - seekers read their sun sign.' },
  { key: 'natal', label: 'Natal', desc: "A personal birth-chart reading from your own chart's transits." },
  { key: 'community', label: 'Community', desc: "The collective pulse - the day's most-pulled card + community vibes." },
]
const LABEL_FIELDS = ['drop_horoscope_alias', 'share_label_alias', 'drop_title_type',
  'horoscope_day_label', 'horoscope_week_label', 'horoscope_month_label']

const setting = ref(null)
const group = ref({})
const loading = ref(false)
const loadError = ref('')
const saveError = ref('')
const savedKey = ref(null)
const switching = ref(false)
const simulating = ref(false)
const preview = ref(null)

const hasBirth = computed(() => !!store.reader?.birth_date)
const focusActive = computed(() => studioAssistantState.open && studioAssistantState.activeField?.field === 'pro:focus_intent')
const illoActive = computed(() => studioAssistantState.open && studioAssistantState.activeField?.field === 'pro:illustration')

function authHeaders() {
  const token = localStorage.getItem('authToken')
  return token ? { Authorization: `Token ${token}` } : {}
}
function flashSaved(key) { savedKey.value = key; setTimeout(() => { if (savedKey.value === key) savedKey.value = null }, 1500) }

function refineFocus() {
  dispatchFieldContext({
    field: 'pro:focus_intent', label: 'Reading Focus & Tone', panel: 'syndicate',
    value: setting.value?.focus_intent || '', source: 'click',
  })
}
function refineIllo() {
  dispatchFieldContext({
    field: 'pro:illustration', label: 'Illustration', panel: 'syndicate',
    value: setting.value?.illustration_instructions || '', source: 'click',
  })
}
function onPolishApplied(e) {
  const { field, value } = e.detail || {}
  if (!setting.value || value === undefined) return
  if (field === 'pro:focus_intent') {
    setting.value.focus_intent = value
    saveSetting({ focus_intent: value }, 'focus')
  } else if (field === 'pro:illustration') {
    setting.value.illustration_instructions = value
    saveSetting({ illustration_instructions: value }, 'illustration')
  }
}

async function saveSetting(body, key) {
  if (!setting.value) return
  saveError.value = ''
  try {
    const res = await axios.patch(`${static_store.apiUrl}/reader/update-drop-setting/${setting.value.id}/`, body, { headers: authHeaders() })
    Object.assign(setting.value, res.data)
    flashSaved(key)
  } catch (error) { saveError.value = parseAxiosError(error) }
}

async function chooseType(type) {
  if (switching.value || setting.value.drop_type === type) return
  switching.value = true
  try {
    const res = await axios.patch(`${static_store.apiUrl}/reader/update-drop-setting/${setting.value.id}/`, { drop_type: type }, { headers: authHeaders() })
    Object.assign(setting.value, res.data)
  } catch (error) { saveError.value = parseAxiosError(error) } finally { switching.value = false }
}

async function saveLabels() {
  if (!group.value.id) return
  const body = {}
  LABEL_FIELDS.forEach(f => { body[f] = group.value[f] || '' })
  try {
    await axios.patch(`${static_store.apiUrl}/reader/update-drop-group/${group.value.id}/`, body, { headers: authHeaders() })
    flashSaved('labels')
  } catch (error) { saveError.value = parseAxiosError(error) }
}

async function simulate() {
  if (simulating.value || !setting.value) return
  simulating.value = true
  saveError.value = ''
  try {
    const res = await axios.post(`${static_store.apiUrl}/reader/pro-drop-preview/`,
      { setting_id: setting.value.id, session_id: store.session_id }, { headers: authHeaders() })
    preview.value = res.data
  } catch (error) { saveError.value = parseAxiosError(error) } finally { simulating.value = false }
}

async function load() {
  loading.value = true
  try {
    const res = await axios.get(`${static_store.apiUrl}/reader/my-drop-settings/`, { headers: authHeaders() })
    const groups = res.data.groups || []
    for (const g of groups) {
      const s = (g.settings || []).find(x => !['affirmation', 'signature'].includes(x.drop_type))
      if (s) { setting.value = s; group.value = g; break }
    }
  } catch (error) { loadError.value = parseAxiosError(error) } finally { loading.value = false }
}

onMounted(() => { window.addEventListener('studio-assistant:polish-applied', onPolishApplied); load() })
onBeforeUnmount(() => window.removeEventListener('studio-assistant:polish-applied', onPolishApplied))
</script>

<style scoped>
@import '@/styles/studio-forms.css';

.pro-types { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 10px; margin-bottom: 14px; }
.pro-type { text-align: left; padding: 12px 14px; border-radius: 10px; cursor: pointer; font-family: inherit; background: rgba(0,0,0,0.22); border: 1px solid rgba(255,255,255,0.12); display: flex; flex-direction: column; gap: 4px; transition: border-color 0.15s, background 0.15s; }
.pro-type:hover { border-color: rgba(201,169,78,0.4); }
.pro-type.on { border-color: rgba(201,169,78,0.7); background: rgba(201,169,78,0.1); }
.pro-type:disabled { opacity: 0.6; cursor: default; }
.pro-type-name { font-family: 'Cinzel', serif; font-size: 14px; color: #C9A94E; font-weight: 600; }
.pro-type-desc { font-size: 11.5px; color: rgba(195,169,255,0.6); line-height: 1.4; }
.pro-warn { margin: 0 0 14px; padding: 10px 12px; font-size: 12.5px; color: #e8c98a; background: rgba(201,169,78,0.1); border: 1px solid rgba(201,169,78,0.3); border-radius: 8px; }
.pro-toggle { display: flex; align-items: center; gap: 9px; margin: 0 0 16px; font-size: 13px; color: #E8D8FF; cursor: pointer; }
.pro-toggle input { accent-color: #C9A94E; }

.beat-field { margin-bottom: 16px; }
.beat-card { box-sizing: border-box; background: rgba(0,0,0,0.22); border: 1px solid rgba(255,255,255,0.12); border-radius: 10px; font-family: 'Inter', sans-serif; }
.beat-card-active { border-color: rgba(201,169,78,0.65); box-shadow: 0 0 0 1px rgba(201,169,78,0.25); }
.beat-card-header { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 14px 16px; }
.beat-head-left { display: inline-flex; align-items: center; gap: 8px; min-width: 0; }
.beat-label-text { font-family: 'Cinzel', serif; font-size: 16px; letter-spacing: 0.05em; text-transform: uppercase; color: #C9A94E; font-weight: 600; }
.beat-label-help { display: inline-flex; align-items: center; margin-left: 6px; }
.beat-card-body { padding: 0 16px 14px; }
.beat-help { margin: 0 0 10px; font-size: 12px; color: rgba(195,169,255,0.45); line-height: 1.5; font-style: italic; }
.beat-card-text { display: block; color: #E8D8FF; font-size: 14px; line-height: 1.6; white-space: pre-wrap; }
.beat-card-empty { display: block; color: rgba(255,255,255,0.35); font-style: italic; font-size: 14px; }
.beat-textarea {
  width: 100%; box-sizing: border-box; resize: vertical; min-height: 64px;
  background: rgba(0, 0, 0, 0.3); border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 8px; padding: 10px 12px; color: #E8D8FF;
  font-family: 'Inter', sans-serif; font-size: 14px; line-height: 1.6; outline: none;
  transition: border-color 0.15s;
}
.beat-textarea::placeholder { color: rgba(255, 255, 255, 0.3); font-style: italic; }
.beat-textarea:focus { border-color: rgba(201, 169, 78, 0.5); }
.beat-card-actions { display: flex; align-items: center; gap: 10px; margin-top: 14px; flex-wrap: wrap; }
.beat-btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; border-radius: 8px; font-size: 13px; font-family: 'Inter', sans-serif; cursor: pointer; border: 1px solid transparent; transition: background 0.15s, border-color 0.15s; }
.beat-btn-refine { background: rgba(201,169,78,0.14); border-color: rgba(201,169,78,0.4); color: #E8DCB4; }
.beat-btn-refine:hover { background: rgba(201,169,78,0.24); border-color: #C9A94E; }
.beat-btn-sim { background: rgba(102,51,153,0.28); border-color: rgba(160,120,220,0.5); color: #E8DDFF; }
.beat-btn-sim:hover { background: rgba(102,51,153,0.42); }
.beat-btn-sim:disabled { opacity: 0.6; cursor: default; }
.beat-card-saved { font-size: 11px; color: #50C7A8; }

.pro-labels { margin-top: 8px; padding-top: 14px; border-top: 1px solid rgba(255,255,255,0.08); }
.pro-labels-head { font-family: 'Cinzel', serif; font-size: 14px; color: #C9A94E; margin: 0 0 4px; }
.pro-label-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 12px; margin-top: 8px; }
.pro-label { display: flex; flex-direction: column; gap: 5px; font-size: 12px; color: rgba(195,169,255,0.7); }

.pro-sim { position: fixed; inset: 0; z-index: 1000; background: rgba(8,4,20,0.7); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; padding: 24px; }
.pro-sim-card { position: relative; max-width: 480px; width: 100%; background: radial-gradient(600px 300px at 50% 0%, #160a2e 0%, #0a0015 70%); border: 1px solid rgba(201,169,78,0.3); border-radius: 16px; padding: 30px 30px 32px; text-align: center; }
.pro-sim-close { position: absolute; top: 12px; right: 14px; background: rgba(0,0,0,0.35); border: none; color: #e9dcff; width: 30px; height: 30px; border-radius: 50%; font-size: 18px; cursor: pointer; }
.pro-sim-label { font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase; color: #9b86c9; margin-bottom: 12px; }
.pro-sim-title { font-family: 'Cinzel', serif; font-size: 20px; color: #FFDFA3; margin: 0 0 12px; }
.pro-sim-teaser { font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic; font-size: 18px; color: #f3e9d2; margin: 0 0 14px; }
.pro-sim-reading { font-size: 14px; line-height: 1.7; color: rgba(233,220,255,0.85); margin: 0; }
</style>
